import { useMemo } from 'react';

import { useMemberId } from '@trello/authentication';

import type { AllBoardsAndWorkspacesQuery } from './AllBoardsAndWorkspacesQuery.generated';
import { useAllBoardsAndWorkspacesQuery } from './AllBoardsAndWorkspacesQuery.generated';
import type { BoardOrganization } from './WorkspaceSwitcher.types';

type Board = NonNullable<
  AllBoardsAndWorkspacesQuery['member']
>['boards'][number];
type Organization = NonNullable<
  AllBoardsAndWorkspacesQuery['member']
>['organizations'][number];

const getGuestOrganizationsForMember = ({
  boards,
  organizations,
}: {
  boards: Board[];
  organizations: Organization[];
}): BoardOrganization[] => {
  const guestOrganizationsMap: Record<string, BoardOrganization> = {};
  const organizationIds = organizations.map((org: { id: string }) => org.id);
  const guestBoards = boards.filter(
    ({ idOrganization }) =>
      idOrganization && !organizationIds.includes(idOrganization),
  );
  for (const board of guestBoards) {
    if (
      // is not null or undefined
      board.idOrganization &&
      // is not already in our guest organizations map
      !guestOrganizationsMap[board.idOrganization] &&
      // and has the organization subdocument. Note: this is required
      // because there was a rare bug where board.idOrganization was
      // defined but board.organization was undefined
      board.organization
    ) {
      guestOrganizationsMap[board.idOrganization] =
        board.organization as unknown as BoardOrganization;
    }
  }

  return Object.values(guestOrganizationsMap);
};

interface AllBoardsAndWorkspaces {
  data: ReturnType<typeof useAllBoardsAndWorkspacesQuery>['data'];
  loading: ReturnType<typeof useAllBoardsAndWorkspacesQuery>['loading'];
  error: ReturnType<typeof useAllBoardsAndWorkspacesQuery>['error'];
  refetch: ReturnType<typeof useAllBoardsAndWorkspacesQuery>['refetch'];
  guestOrganizations: ReturnType<typeof getGuestOrganizationsForMember>;
}

export const useAllBoardsAndWorkspaces = (): AllBoardsAndWorkspaces => {
  const memberId = useMemberId();
  // eslint-disable-next-line @trello/no-apollo-refetch
  const { data, loading, error, refetch } = useAllBoardsAndWorkspacesQuery({
    variables: { memberId },
    waitOn: ['MemberHeader', 'MemberBoards'],
  });

  const guestOrganizations = useMemo(() => {
    return data?.member?.boards && data?.member?.organizations
      ? getGuestOrganizationsForMember({
          boards: data?.member?.boards,
          organizations: data?.member?.organizations,
        })
      : [];
  }, [data?.member?.boards, data?.member?.organizations]);

  return {
    guestOrganizations,
    data,
    loading,
    error,
    refetch,
  };
};
